import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PackageSetsList } from '../../../multi-package-modal/package-set-list';
import { messages } from './packages-panel-messages';
import style from './packages-panel.module.css';
import { PackageCard } from '../package-card/package-card';

import type { PackageSet } from '@shipstation/client-common/types/seller-data';

import type { Package } from '../../types';

const PACKAGE_QUANTITY_OPTIONS = [5, 10, 15];

type PackagesPanelProps = {
  packages: Package[];
  expandedPackageIndex: number | null;
  onToggleExpand: (index: number) => void;
  onAddPackages: (quantity: number) => void;
  onDuplicatePackage: (index: number) => void;
  onRemovePackage: (index: number) => void;
  packageSetsList?: PackageSet[];
  applyPackageSetFromList?: (packageSet: PackageSet) => void;
};

export const PackagesPanel = ({
  packages,
  expandedPackageIndex,
  onToggleExpand,
  onAddPackages,
  onDuplicatePackage,
  onRemovePackage,
  packageSetsList = [],
  applyPackageSetFromList,
}: PackagesPanelProps) => {
  const hasPackageSets = packageSetsList.length > 0;

  return (
    <FlexColumn gap={Gap.Space16} className={style['packages-panel']}>
      <FlexRow gap={Gap.Space12} className={style.header}>
        <div>
          <Text textType={TextType.H3} value={messages.packagesTitle} />
          <Text textType={TextType.Paragraph} value={messages.packagesInstruction} />
        </div>
        <FlexRow gap={Gap.Space8}>
          {hasPackageSets && applyPackageSetFromList && (
            <PackageSetsList applyPackageSetFromList={applyPackageSetFromList} packageSetsList={packageSetsList} />
          )}
          <FlexRow className={style['add-package-button-container']}>
            <Button className={style['add-package-button']} onClick={() => onAddPackages(1)} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.addPackage} />
            </Button>
            <DropdownMenu>
              <DropdownMenuToggler theme={THEME_CONSTANTS.primary} />
              {PACKAGE_QUANTITY_OPTIONS.map((packageOption) => (
                <DropdownMenuItem key={packageOption} onClick={() => onAddPackages(packageOption)}>
                  <FormattedMessage {...messages.addPackagesLabel} values={{ numberOfPackages: packageOption }} />
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </FlexRow>
        </FlexRow>
      </FlexRow>

      <div className={style['packages-grid']}>
        {packages.map((pack, index) => (
          <PackageCard
            key={pack.id}
            pack={pack}
            packageIndex={index}
            isExpanded={expandedPackageIndex === index}
            onToggleExpand={() => onToggleExpand(index)}
            onDuplicate={() => onDuplicatePackage(index)}
            onRemove={() => onRemovePackage(index)}
          />
        ))}
      </div>
    </FlexColumn>
  );
};
